import { ProjectDescriptionService } from "./ProjectDescriptionService";
import { TechnicalSolutionService } from "./TechnicalSolutionService";
import { MilestonesService } from "./MilestonesService";
import { UserStoriesService } from "./UserStoriesService";
import { SchemaService } from "./SchemaService";
import type {
  ExpandDescriptionRequest,
  ExpandDescriptionResponse,
  ExpandTechnicalDescriptionRequest,
  ExpandTechnicalDescriptionResponse,
  TechnicalSolution,
  GenerateMilestonesRequest,
  GenerateMilestonesResponse,
  GenerateUserStoriesRequest,
  GenerateUserStoriesResponse,
  GenerateSchemaRequest,
  GenerateSchemaResponse,
} from "@/features/Projects";

class GeminiService {
  private readonly projectDescriptionService: ProjectDescriptionService;
  private readonly technicalSolutionService: TechnicalSolutionService;
  private readonly milestonesService: MilestonesService;
  private readonly userStoriesService: UserStoriesService;
  private readonly schemaService: SchemaService;

  constructor() {
    this.projectDescriptionService = new ProjectDescriptionService();
    this.technicalSolutionService = new TechnicalSolutionService();
    this.milestonesService = new MilestonesService();
    this.userStoriesService = new UserStoriesService();
    this.schemaService = new SchemaService();
  }

  // Configuration check
  isConfigured(): boolean {
    return !!import.meta.env.VITE_REACT_GEMINI_API_KEY;
  }

  // Project Description Methods
  async expandProjectDescription(
    request: ExpandDescriptionRequest
  ): Promise<ExpandDescriptionResponse> {
    return this.projectDescriptionService.expandProjectDescription(request);
  }

  async generateObjectives(projectDescription: string): Promise<string[]> {
    return this.projectDescriptionService.generateObjectives(projectDescription);
  }

  // Technical Solution Methods
  async generateTechnicalSolution(
    technicalDescription: string,
    objectives: string[],
    projectContext?: {
      projectDescription: string;
      projectName: string;
    }
  ): Promise<TechnicalSolution> {
    return this.technicalSolutionService.generateTechnicalSolution(
      technicalDescription,
      objectives,
      projectContext
    );
  }

  async expandTechnicalDescription(
    request: ExpandTechnicalDescriptionRequest
  ): Promise<ExpandTechnicalDescriptionResponse> {
    return this.technicalSolutionService.expandTechnicalDescription(request);
  }

  // Milestones Methods
  async generateMilestones(
    request: GenerateMilestonesRequest
  ): Promise<GenerateMilestonesResponse> {
    return this.milestonesService.generateMilestones(request);
  }

  // User Stories Methods
  async generateUserStories(
    request: GenerateUserStoriesRequest
  ): Promise<GenerateUserStoriesResponse> {
    return this.userStoriesService.generateUserStories(request);
  }

  // Schema Methods
  async generateSchema(
    request: GenerateSchemaRequest
  ): Promise<GenerateSchemaResponse> {
    return this.schemaService.generateSchema(request);
  }

  async generateDataModels(request: {
    projectDescription: string;
    objectives: string[];
    technicalContext: string;
    storiesContext: string;
    databasePreference: string;
  }): Promise<any[]> {
    return this.schemaService.generateDataModels(request);
  }

  async generateApiEndpoints(request: {
    projectDescription: string;
    objectives: string[];
    dataModels: any[];
    apiStyle: string;
    authRequired: boolean;
    technicalSolution?: any;
    stories?: any;
    timeline?: any;
  }): Promise<any[]> {
    return this.schemaService.generateApiEndpoints(request);
  }
}

export const geminiService = new GeminiService();
export default geminiService;